import { ReactElement, ReactNode } from 'react';
import { TableProps as MuiTableProps, SxProps } from '@mui/material';
import { FilterItem, Operator, UrlFilters } from '../../hooks/useUrlFilters';

export enum ColumnType {
  Date = 'date',
  Text = 'text',
}

export type ColumnFilterOption = {
  label: string;
  value: string;
  count?: number;
  icon?: ReactNode;
};

export type StickyColumnSide = 'left' | 'right';

export type StickyColumnPosition = {
  side: StickyColumnSide;
  offset: number;
  hasDivider: boolean;
};

export type StickyColumnsLayout = {
  positions: Record<string, StickyColumnPosition | undefined>;
  selectionColumn?: StickyColumnPosition;
  expandColumn?: StickyColumnPosition;
};

export type Column<T> = {
  id: string;
  header: string;
  valueKey?: string;
  showSelectAll?: boolean;
  renderer?: (rowData: T) => JSX.Element;
  sortable?: boolean;
  compareFunc?: (a: T, b: T) => number;
  filterable?: boolean;
  filterRenderer?: (value: string) => string;
  type?: ColumnType;
  emptyValueLabel?: string;
  filterOptionsOverride?: ColumnFilterOption[];
  filterOperators?: Operator[];
  filterValueMatcher?: (
    selectedFilterValue: string[],
    rowData: T,
    operator: Operator,
  ) => boolean;
  hideColumnFilter?: boolean;
  sortFilterOptions?: boolean;
  width?: number | string;
  filterDropdownWidth?: number;
  centerContent?: boolean;
  metaColumn?: boolean;
  sticky?: StickyColumnSide;
  hideColumn?: boolean;
  nonToggleable?: boolean;
  defaultUntoggled?: boolean;
  showValueWhenEmpty?: boolean;
  isFilterCounterHidden?: boolean;
  hideFromTableFilters?: boolean;
};

export type QuickFilter = {
  name: string;
  filters: FilterItem[];
  icon?: ReactElement;
  tooltip?: string;
  loading?: boolean;
};

export type GroupedData<T> = {
  title: string;
  icon?: ReactNode;
  count?: number;
  metadataRenderer?: () => ReactNode;
  data: Array<T>;
  isLoading?: boolean;
};

export type GroupByProps<T> = {
  groupBy?: string | null;
  onGroupByChange?: (columnId: string | null) => void;
  groupableColumnIds?: string[];
  groupedData?: GroupedData<T>[];
  expandedGroups?: Record<string, boolean>;
  onGroupToggle?: (groupTitle: string) => void;
  onGroupScrollToBottom?: (groupTitle: string) => void;
};

type BaseVirtualRow = {
  type: string;
  groupTitle: string;
};

export type GroupHeaderVirtualRow<T> = BaseVirtualRow & {
  type: 'group-header';
  group: GroupedData<T>;
};

export type DataVirtualRow<T> = BaseVirtualRow & {
  type: 'data';
  data: T;
  indexInGroup: number;
};

export type GroupScrollMarkerVirtualRow = BaseVirtualRow & {
  type: 'group-scroll-marker';
};

export type GroupLoadingVirtualRow = BaseVirtualRow & {
  type: 'group-loading';
};

export type FlattenedRow<T> =
  | GroupHeaderVirtualRow<T>
  | DataVirtualRow<T>
  | GroupScrollMarkerVirtualRow
  | GroupLoadingVirtualRow;

export type SortDirection = 'asc' | 'desc';

export type BaseTableProps<T> = MuiTableProps & {
  columns: Array<Column<T>>;
  height?: number | string;
  width?: number;
  initialSelectedFilters?: UrlFilters;
  hidePagination?: boolean;
  expandedRowRenderer?: (rowData: T) => JSX.Element | null;
  expandCondition?: (rowData: T) => boolean;
  selectionColumn?: boolean | ((rowData: T) => boolean);
  selectionDisabled?: boolean;
  defaultSortKey?: string;
  defaultSortDirection?: SortDirection;
  emptyStateRenderer?: () => JSX.Element;
  selectedItems?: string[];
  onSelectItem?: (items: string[]) => void;
  isSelectAll?: boolean;
  onSelectAllChange?: (isSelectAll: boolean) => void;
  excludedItems?: string[];
  onExcludeItem?: (items: string[]) => void;
  onFilteredItemsChange?: (data: T[]) => void;
  idKey?: string;
  sx?: SxProps;
  wrapperSx?: SxProps;
  bottomScrollPadding?: number;
  urlParamsPrefix?: string;
  search?: string;
  searchFunc?: (search: string, rowData: T) => boolean;
  animateRows?: string[];
  focusedRows?: string[];
  centerRowCell?: boolean;
  onRowClick?: (rowData: T) => void;
  localStorageKey?: string;
  isLoading?: boolean;
  onScrollToBottom?: () => void;
  isControlled?: boolean;
  isFilterOptionsLoading?: boolean;
};
